// LightWithFade.js
import { useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { RectAreaLightUniformsLib } from "three/examples/jsm/lights/RectAreaLightUniformsLib.js";

export default function LightWithFade({
  type = "ambient",
  start = true,
  duration = 1,
  finalIntensity = 25,
  color = "white",
  position = [0, 0, 0],
  rotation = null,
  easing = false,
  debug = false,
  ...props
}) {
  const lightRef = useRef(null);
  const elapsed = useRef(0);

  useEffect(() => {
    if (type === "rectArea") {
      try {
        RectAreaLightUniformsLib.init();
      } catch (e) {
        if (debug) console.warn("RectAreaLightUniformsLib.init() failed:", e);
      }
    }
  }, [type, debug]);

  // reset on start
  useEffect(() => {
    elapsed.current = 0;
    if (lightRef.current) lightRef.current.intensity = 0;
  }, [start]);

  // animation
  useFrame((_, delta) => {
    if (!start || !lightRef.current) return;
    if (elapsed.current >= duration) return;

    elapsed.current += delta;
    let t = Math.min(elapsed.current / duration, 1);
    if (easing) t = t * t * (3 - 2 * t); // smoothstep
    lightRef.current.intensity = t * finalIntensity;
  });

  // selon type -> retourner la lumière correspondante
  switch (type) {
    case "rectArea":
      return (
        <rectAreaLight
          ref={lightRef}
          args={[color, 0, props.width ?? 1, props.height ?? 1]}
          position={position}
          rotation={rotation}
          {...props}
        />
      );
    case "directional":
      return <directionalLight ref={lightRef} args={[color, 0]} position={position} rotation={rotation} {...props} />;
    case "spot":
      return <spotLight ref={lightRef} args={[color, 0]} position={position} rotation={rotation} {...props} />;
    case "point":
      return <pointLight ref={lightRef} args={[color, 0]} position={position} {...props} />;
    case "hemisphere":
      return <hemisphereLight ref={lightRef} args={[color, props.skyColor ?? "white", props.groundColor ?? "gray"]} position={position} {...props} />;
    default:
      return <ambientLight ref={lightRef} args={[color, 0]} />;
  }
}
